import { useCallback, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import DOMPurify from 'dompurify';
import { ChevronRightIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useSnapshot } from 'valtio';

import CampaignAfterCloseMessage from '../../../components/pages/customer-portal/campaign-after-close-message.jsx';
import CampaignCountdown, {
  campaignDateMs,
} from '../../../components/pages/customer-portal/campaign-countdown.jsx';
import PortalCardWrapper from '../../../components/pages/customer-portal/card-wrapper.jsx';
import PortalContainer from '../../../components/pages/customer-portal/container.jsx';
import FormJoinCampaign from '../../../components/pages/customer-portal/forms/form-join-campaign.jsx';
import PortalSubHeading from '../../../components/pages/customer-portal/heading-sub.jsx';
import PortalHeading from '../../../components/pages/customer-portal/heading.jsx';
import PortalLayoutCampaign from '../../../components/pages/customer-portal/layout-campaign.jsx';
import ContactsInfo from '../../../components/contacts-info.jsx';
import Error from '../../../components/feedback/error.jsx';
import Loading from '../../../components/feedback/loading.jsx';
import JoinDoorIcon from '../../../components/icons/join-door.jsx';
import UserPlusIcon from '../../../components/icons/user-plus.jsx';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '../../../components/ui/avatar.jsx';
import { Button } from '../../../components/ui/button.jsx';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../../components/ui/dialog.jsx';
import { Input } from '../../../components/ui/input.jsx';
import RichTextViewer from '../../../components/ui/rich-text-viewer.jsx';
import { authState } from '../../../services/state/auth-state.jsx';
import { useQueryActiveCampaign } from '../../../hooks/queries/use-active-campaign.jsx';
import { useMediaQuery } from '../../../hooks/use-media-query.jsx';
import {
  CAMPAIGN_STATES,
  CATALYST_USER_ROLES,
} from '../../../utils/constants.js';

import '../../../styles/customer-portal.css';

export function CustomerPortalCampaignsShow() {
  const { user } = useSnapshot(authState);
  const isDesktop = useMediaQuery('(min-width: 768px)');

  const queryActiveCampaign = useQueryActiveCampaign();
  const queryClient = useQueryClient();
  const campaign = queryActiveCampaign.data;
  const campaignKey = `${campaign?.ROWID}:${campaign?.closeDate}`;
  const [closedCampaignKey, setClosedCampaignKey] = useState(null);
  const closeMs = campaignDateMs(campaign?.closeDate);
  const isClosedByTime =
    !!campaign &&
    (closedCampaignKey === campaignKey ||
      !Number.isFinite(closeMs) ||
      Date.now() >= closeMs);
  const handleCampaignClosed = useCallback(() => {
    setClosedCampaignKey(campaignKey);
    void queryClient.invalidateQueries({ queryKey: ['active_campaign'] });
  }, [campaignKey, queryClient]);

  const [filterQuery, setFilterQuery] = useState('');

  const filteredShops = queryActiveCampaign?.data?.shops.filter((item) =>
    item.name.toLowerCase().includes(filterQuery.toLowerCase()),
  );

  return (
    <PortalContainer>
      {queryActiveCampaign.isLoading ? (
        <div className="flex h-[70dvh] items-center justify-center">
          <Loading />
        </div>
      ) : queryActiveCampaign.isError && !campaign ? (
        <Error />
      ) : !queryActiveCampaign.data ? (
        <div className="flex flex-col items-center justify-center">
          <div className="relative aspect-[9/16] overflow-hidden rounded-[40px] bg-gray-200 md:aspect-[16/9]">
            <img
              src={
                isDesktop
                  ? './assets/no-active-campaign_1920X1080px.png'
                  : './assets/no-active-campaign_1080X1920px.png'
              }
              alt="Não existe nenhuma campanha ativa"
              className="h-full w-full object-cover"
              loading="eager"
            />
          </div>

          <ContactsInfo />
        </div>
      ) : isClosedByTime ||
        queryActiveCampaign.data?.state === CAMPAIGN_STATES.POST_END_DATE ||
        queryActiveCampaign.data?.state === CAMPAIGN_STATES.READY_TO_DRAW ||
        queryActiveCampaign.data?.state === CAMPAIGN_STATES.DRAW_CONCLUDED ? (
        <div className="flex flex-col items-center justify-center">
          {(isClosedByTime ||
            queryActiveCampaign.data?.state ===
              CAMPAIGN_STATES.POST_END_DATE) && (
            <CampaignAfterCloseMessage
              regulationUrl={queryActiveCampaign?.data?.regulationUrl}
            />
          )}

          <div className="relative aspect-[9/16] overflow-hidden rounded-[40px] bg-gray-200 md:aspect-[16/9]">
            <img
              src={
                isDesktop
                  ? queryActiveCampaign.data?.heroNotActiveXlUrl ||
                    './assets/no-active-campaign_1920X1080px.png'
                  : queryActiveCampaign.data?.heroNotActiveSmUrl ||
                    './assets/no-active-campaign_1080X1920px.png'
              }
              alt="Campanha expirada"
              className="h-full w-full object-cover"
              loading="eager"
            />
          </div>

          <ContactsInfo />
        </div>
      ) : (
        <>
          <CampaignCountdown
            onExpire={handleCampaignClosed}
            closeDate={queryActiveCampaign?.data?.closeDate}
            countdownStartDate={queryActiveCampaign?.data?.countdownStartDate}
          />
          <PortalLayoutCampaign
            campaignBanner={queryActiveCampaign?.data.bannerUrl}
            campaignName={queryActiveCampaign?.data.name}
          >
            {/* Campaign Conditions */}
            <PortalCardWrapper>
              <div>
                <PortalHeading className="font-title mb-1">
                  {queryActiveCampaign?.data?.name}
                </PortalHeading>

                <div
                  className="font-title rich-text-class pt-2 text-portal-text"
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      queryActiveCampaign?.data?.description,
                    ),
                  }}
                />
              </div>

              {!user && (
                <div className="flex flex-col items-center gap-y-4 2xl:flex-row 2xl:justify-between">
                  <Link to="/auth/register">
                    <Button variant="portal">
                      <UserPlusIcon />
                      Criar conta cliente
                    </Button>
                  </Link>

                  <Link to="/auth/login">
                    <Button variant="portal">
                      <JoinDoorIcon />
                      Já tenho conta. Entrar aqui
                    </Button>
                  </Link>
                </div>
              )}

              <div className="flex flex-col gap-4">
                <RichTextViewer
                  removeClass
                  className="rich-text-class text-portal-text"
                  value={queryActiveCampaign?.data?.rules}
                />
              </div>

              {queryActiveCampaign?.data?.adherentShopsVisible ? (
                <div className="flex flex-col gap-4">
                  <PortalSubHeading>Lojas Aderentes</PortalSubHeading>
                  <div className="flex flex-wrap items-center gap-4">
                    {queryActiveCampaign?.data?.shops
                      .slice(0, 4)
                      .map((item, i) => {
                        return (
                          <Avatar key={i} className="size-12 rounded-full">
                            <AvatarImage
                              className="object-cover"
                              src={item.image}
                            />
                            <AvatarFallback>?</AvatarFallback>
                          </Avatar>
                        );
                      })}

                    {queryActiveCampaign?.data?.shops?.length > 4 && (
                      <Dialog>
                        <DialogTrigger asChild>
                          <Button className="h-12 rounded-full bg-portal-primary px-4 font-noto-ikea-bold text-xs font-bold text-black hover:bg-portal-primary/80">
                            <span>Ver todas</span>
                            <ChevronRightIcon />
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="w-full max-w-lg md:max-w-3xl lg:max-w-4xl xl:max-w-5xl">
                          <DialogHeader>
                            <DialogTitle>Lojas Aderentes</DialogTitle>
                            <DialogDescription>
                              Lojas aderentes à campanha{' '}
                              {queryActiveCampaign?.data?.name}
                            </DialogDescription>
                          </DialogHeader>
                          <Input
                            placeholder="Procurar loja..."
                            onChange={(e) => setFilterQuery(e.target.value)}
                            value={filterQuery}
                          />

                          <div className="h-[500px] overflow-auto">
                            <div className="mt-2 grid grid-cols-1 flex-col items-start gap-6 pr-2 lg:grid-cols-4">
                              {filteredShops.map((item, i) => (
                                <div
                                  key={i}
                                  className="flex flex-row items-center gap-4"
                                >
                                  <Avatar className="size-10 rounded-lg">
                                    <AvatarImage
                                      className="object-cover"
                                      src={item.image}
                                    />
                                    <AvatarFallback>?</AvatarFallback>
                                  </Avatar>

                                  <div className="flex flex-col">
                                    <span>{item.name}</span>
                                    <DialogDescription className="line-clamp-1">
                                      {item.type}
                                    </DialogDescription>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    )}
                  </div>
                </div>
              ) : null}
            </PortalCardWrapper>

            {/* Participate in the campaign */}
            {user && user.details.role === CATALYST_USER_ROLES.customer && (
              <PortalCardWrapper>
                <PortalHeading id="join_campaign">
                  Participar na campanha
                </PortalHeading>

                <FormJoinCampaign
                  queryActiveCampaign={queryActiveCampaign}
                  onCampaignClosed={handleCampaignClosed}
                  scrollId="join_campaign"
                />
              </PortalCardWrapper>
            )}
          </PortalLayoutCampaign>
        </>
      )}
    </PortalContainer>
  );
}
